/**
 * Room Configuration Utility
 *
 * Manages room identification for different terminals/devices in the lab.
 * Uses hybrid approach: Backend as primary source, localStorage as fallback/cache.
 */

import { deviceService } from '@/domains/internal/facilities/services/deviceService';

export interface RoomConfig {
  roomId: string | null;
  roomName: string | null;
  departmentId: string | null;
  assignedAt: string | null;
}

const STORAGE_KEY = 'adlabs_room_config';
const SKIP_KEY = 'adlabs_room_setup_skipped';

// In-memory cache to prevent excessive API calls
let roomConfigCache: RoomConfig | null = null;
let cacheTimestamp: number | null = null;
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

// Request deduplication - store in-flight promise
let inflightRequest: Promise<RoomConfig> | null = null;

/**
 * Get current room configuration
 * Priority: Backend API > localStorage > URL params > Environment variables
 */
export const getRoomConfig = (): RoomConfig => {
  // For synchronous usage, return from localStorage
  // The async version (getRoomConfigAsync) should be preferred for initial load
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch (e) {
      console.error('Failed to parse room config:', e);
    }
  }

  // Check URL parameters (for initial setup or override)
  const urlParams = new URLSearchParams(window.location.search);
  const urlRoom = urlParams.get('room');
  const urlRoomName = urlParams.get('room_name');
  const urlDepartment = urlParams.get('department');

  if (urlRoom) {
    const config: RoomConfig = {
      roomId: urlRoom,
      roomName: urlRoomName || urlRoom,
      departmentId: urlDepartment,
      assignedAt: new Date().toISOString(),
    };
    setRoomConfig(config);
    // Clean URL without reloading
    window.history.replaceState({}, document.title, window.location.pathname);
    return config;
  }

  // Check environment variables (fallback)
  const envRoomId = import.meta.env.VITE_ROOM_ID;
  const envRoomName = import.meta.env.VITE_ROOM_NAME;
  const envDepartmentId = import.meta.env.VITE_DEPARTMENT_ID;

  if (envRoomId) {
    const config: RoomConfig = {
      roomId: envRoomId,
      roomName: envRoomName || envRoomId,
      departmentId: envDepartmentId || null,
      assignedAt: new Date().toISOString(),
    };
    setRoomConfig(config);
    return config;
  }

  // No room assigned
  return {
    roomId: null,
    roomName: null,
    departmentId: null,
    assignedAt: null,
  };
};

/**
 * Check if in-memory cache is still valid
 */
const isCacheValid = (): boolean => {
  if (!roomConfigCache || !cacheTimestamp) {
    return false;
  }
  const now = Date.now();
  return (now - cacheTimestamp) < CACHE_DURATION_MS;
};

/**
 * Invalidate the in-memory cache (called when room is manually changed)
 */
export const invalidateRoomCache = (): void => {
  roomConfigCache = null;
  cacheTimestamp = null;
  inflightRequest = null;
};

/**
 * Get current room configuration (async)
 * IP-first approach (Option A: Last Assignment Wins):
 * 1. Check in-memory cache (5 min TTL)
 * 2. If request already in-flight, return that promise (deduplication)
 * 3. Try IP-based lookup (source of truth for the device)
 * 4. Try fingerprint-based lookup (fallback for specific browser)
 * 5. Fall back to localStorage
 * 6. Fall back to URL params / env vars
 */
export const getRoomConfigAsync = async (skipCache = false): Promise<RoomConfig> => {
  // Return cached value if valid and not explicitly skipping cache
  if (!skipCache && isCacheValid() && roomConfigCache) {
    return roomConfigCache;
  }

  // If a request is already in-flight, return that promise instead of making a new one
  // This prevents multiple concurrent calls from different components
  if (!skipCache && inflightRequest) {
    return inflightRequest;
  }

  // Create a new request and store it for deduplication
  inflightRequest = (async () => {
    try {
      // Priority 1: Try IP-based lookup first (source of truth)
      const deviceInfoByIP = await deviceService.getRoomByIP();

      if (deviceInfoByIP && deviceInfoByIP.room) {
        const config: RoomConfig = {
          roomId: deviceInfoByIP.room.room_id,
          roomName: deviceInfoByIP.room.room_name,
          departmentId: deviceInfoByIP.room.department_id,
          assignedAt: deviceInfoByIP.registered_at || new Date().toISOString(),
        };

        // Update caches
        setRoomConfig(config);
        roomConfigCache = config;
        cacheTimestamp = Date.now();
        return config;
      }
    } catch (error) {
      console.warn('[RoomConfig] IP-based lookup failed, trying fingerprint-based lookup:', error);
    }

    try {
      // Priority 2: Try fingerprint-based lookup (browser-specific fallback)
      const deviceInfo = await deviceService.getDeviceRoom();

      if (deviceInfo && deviceInfo.room) {
        const config: RoomConfig = {
          roomId: deviceInfo.room.room_id,
          roomName: deviceInfo.room.room_name,
          departmentId: deviceInfo.room.department_id,
          assignedAt: deviceInfo.registered_at || new Date().toISOString(),
        };

        // Update caches
        setRoomConfig(config);
        roomConfigCache = config;
        cacheTimestamp = Date.now();
        return config;
      }
    } catch (error) {
      console.warn('[RoomConfig] Fingerprint-based lookup failed, falling back to localStorage:', error);
    }

    // Priority 3 & 4: Fallback to synchronous method (localStorage, URL params, env vars)
    const fallbackConfig = getRoomConfig();

    // Cache the fallback result too
    roomConfigCache = fallbackConfig;
    cacheTimestamp = Date.now();

    return fallbackConfig;
  })();

  try {
    // Wait for the request to complete
    const result = await inflightRequest;
    return result;
  } finally {
    // Clear the in-flight request after it completes (success or failure)
    inflightRequest = null;
  }
};

/**
 * Set room configuration
 */
export const setRoomConfig = (config: RoomConfig): void => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
};

/**
 * Clear room configuration and mark as skipped (for admins who choose "No Room")
 */
export const clearRoomConfig = (): void => {
  localStorage.removeItem(STORAGE_KEY);
  // Mark as skipped in localStorage (clears on logout)
  localStorage.setItem(SKIP_KEY, 'true');
  // Invalidate cache
  invalidateRoomCache();
};

/**
 * Check if user has explicitly skipped room setup
 */
export const hasSkippedRoomSetup = (): boolean => {
  return localStorage.getItem(SKIP_KEY) === 'true';
};

/**
 * Clear the skip flag (called on logout)
 */
export const clearSkipFlag = (): void => {
  localStorage.removeItem(SKIP_KEY);
};

/**
 * Check if room is assigned
 */
export const isRoomAssigned = (): boolean => {
  const config = getRoomConfig();
  return config.roomId !== null;
};

/**
 * Update room configuration (for admin use)
 */
export const updateRoomConfig = (
  roomId: string,
  roomName: string,
  departmentId?: string
): void => {
  const config: RoomConfig = {
    roomId,
    roomName,
    departmentId: departmentId || null,
    assignedAt: new Date().toISOString(),
  };
  setRoomConfig(config);
  // Update in-memory cache
  roomConfigCache = config;
  cacheTimestamp = Date.now();
  // Clear skip flag since a room is now assigned
  clearSkipFlag();
};
